import { Box, SelectChangeEvent, Typography, useTheme } from '@mui/material';
import React from 'react';
import parse from 'html-react-parser';
import { styled } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';

import {
    MANTY_STATUS,
    ORG_CHART_HR_MAPPING,
    ORG_CHART_HR_NAME,
} from 'routes/HumanResourcesModule/business/definitions';
import {
    humanizedRecurrentValues,
    RecurrenceEnum,
} from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import {
    HrSimulation,
    SimulationDateString,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { getPositionValue } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/gridHelpers';
import { orgChartHrFormatter } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import { SimulationAgentData } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/types';
import { FormState } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/AddHypotheses/AgentHypothesisModal/AgentHypothesisModal';
import DateInput from 'components/DateInput';
import AgentDepartureHypothesisTypeSelector from './AgentDepartureHypothesisTypeSelector';
import { AgentDepartureHypothesisType } from '../helpers/constants';
import { isValidDepartureDate } from '../../AddHypotheses/AgentHypothesisModal/helpers';

const StyFormContainer = styled(Box, { name: 'StyFormContainer' })({
    display: 'flex',
    gap: '2rem',
    backgroundColor: 'white',
    padding: '1rem',
    borderRadius: '0.5rem',
    alignItems: 'flex-start',
});

const StyAgentInfos = styled(Box, { name: 'StyAgentsInfo' })(({ theme }) => ({
    display: 'flex',
    gap: '2rem',
    padding: '0.5rem',
    color: theme.palette.darkGrey,
}));

type Props = {
    hrSimulation: HrSimulation;
    agentData: SimulationAgentData;
    formState: FormState;
    setFormState: React.Dispatch<React.SetStateAction<FormState>>;
    recurrence: RecurrenceEnum;
};

const AgentDepartureHypothesisForm = ({
    hrSimulation,
    agentData,
    formState,
    setFormState,
    recurrence,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();
    return (
        <>
            <StyAgentInfos>
                <Typography>
                    {parse(
                        `${t(
                            'humanResources.payState.orgChartHr',
                        )}: <b>${orgChartHrFormatter({
                            orgChartHrName: agentData[ORG_CHART_HR_NAME],
                            orgChartHrMapping: agentData[ORG_CHART_HR_MAPPING],
                        })}</b>`,
                    )}
                </Typography>
                <Typography>
                    <Trans
                        i18nKey="humanResources.simulation.agentClassification"
                        tOptions={{
                            status: getPositionValue(agentData[MANTY_STATUS]),
                            recurrence: humanizedRecurrentValues[recurrence],
                        }}
                        components={{
                            bold: (
                                <strong
                                    style={{
                                        color: theme.palette.darkGrey,
                                    }}
                                />
                            ),
                        }}
                    />
                </Typography>
            </StyAgentInfos>
            <StyFormContainer>
                <AgentDepartureHypothesisTypeSelector
                    value={formState.agentDepartureHypothesisType}
                    onChange={(
                        event: SelectChangeEvent<AgentDepartureHypothesisType>,
                    ) => {
                        // NOTE: we can select all hypothesis types, but for now
                        // the effect is the same: it's a departure
                        const newValue = event.target
                            .value as AgentDepartureHypothesisType;
                        setFormState({
                            ...formState,
                            agentDepartureHypothesisType: newValue,
                        });
                    }}
                    recurrence={recurrence}
                />
                <DateInput
                    value={formState.departureStartDate}
                    onChange={(date) =>
                        setFormState({
                            ...formState,
                            departureStartDate: date as SimulationDateString,
                        })
                    }
                    label={t('humanResources.simulation.applicationDate')}
                    sx={{ flex: 1 }}
                    isDateAllowed={(value) =>
                        value === undefined ||
                        isValidDepartureDate(value, hrSimulation)
                    }
                    helperText={t(
                        'humanResources.simulation.invalidDepartureDate',
                    )}
                    labelOutside
                    inputProps={{
                        min: hrSimulation.startDate,
                        max: hrSimulation.endDate,
                    }}
                />
            </StyFormContainer>
        </>
    );
};

export default AgentDepartureHypothesisForm;
